import { Button, Card, CardBody, Flex, Icon, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiArrowRight, FiEdit3, FiEye } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  PortalLifecycleStatus,
  PublicBidStatus,
  useCreatePublicBidMutation,
  useGetPortalRegistrationStatusQuery,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useAppSelector } from "../../../../common/redux/hooks";
import { usePortalEventUrls } from "../usePortalEventUrls";
import { isReadonlyBidStatus } from "./readonly/readonlyBidStatus";

export const StartBidCta = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const portalUrls = usePortalEventUrls();
  const displayer = useApiError();
  const authState = useAppSelector((state) => state.authState.state);
  const [createBid, { isLoading }] = useCreatePublicBidMutation();

  const isLoggedInWithOrg = authState?.type === "LoggedInWithOrgContext";
  const isOpen = event.lifecycle.status === PortalLifecycleStatus.Open;

  // Registering interest is the entry point; the offer CTA only appears once the supplier is following the procurement.
  const { data: status } = useGetPortalRegistrationStatusQuery(
    { eventId: event.id },
    { skip: !isLoggedInWithOrg || !isOpen || !!event.competitionStage }
  );

  // Two-stage competitions gate the offer on the invitation (ParticipationCta), not registration.
  if (!isLoggedInWithOrg || !isOpen || event.competitionStage || !status?.hasRegistered) return null;

  const openExistingBid = (bidId: string) => navigate(portalUrls.bid.edit.go(event.id, bidId).intro);

  const startNewBid = async () => {
    try {
      const bid = await createBid({ eventId: event.id }).unwrap();
      navigate(portalUrls.bid.edit.go(event.id, bid.id).intro);
    } catch (e) {
      displayer.trigger(e);
    }
  };

  // The registration-status response tells us the org's existing bid deterministically, so the CTA
  // reflects start / continue / view without POSTing to the create-or-resume endpoint to find out.
  const existingBidId = status.currentBidId;
  const cta = deriveCta(existingBidId, status.currentBidStatus, t);

  return (
    <Card variant="filled" bg="smPrimarySofter" rounded="xl">
      <CardBody>
        <Flex direction={{ base: "column", md: "row" }} align={{ md: "center" }} justify="space-between" gap="4">
          <Stack spacing="1">
            <Text fontWeight="semibold">{cta.title}</Text>
            <Text fontSize="sm" color="smMuted">
              {cta.subtitle}
            </Text>
          </Stack>
          <Button
            colorScheme="teal"
            leftIcon={<Icon as={cta.icon} />}
            onClick={existingBidId ? () => openExistingBid(existingBidId) : startNewBid}
            isLoading={isLoading}
            flexShrink={0}
          >
            {cta.button}
          </Button>
        </Flex>
      </CardBody>
    </Card>
  );
};

const deriveCta = (
  currentBidId: string | null | undefined,
  currentBidStatus: PublicBidStatus | null | undefined,
  t: (key: string) => string,
) => {
  if (!currentBidId) {
    return {
      title: t("Want to compete for this contract?"),
      subtitle: t("Build your offer step by step. We save your progress automatically."),
      button: t("Start offer"),
      icon: FiEdit3,
    };
  }
  if (currentBidStatus && isReadonlyBidStatus(currentBidStatus)) {
    return {
      title: t("Your offer has been delivered"),
      subtitle: t("You can view the offer you submitted."),
      button: t("View offer"),
      icon: FiEye,
    };
  }
  return {
    title: t("You have a saved offer"),
    subtitle: t("Continue where you left off — we save your progress automatically."),
    button: t("Continue offer"),
    icon: FiArrowRight,
  };
};
